import { VehicleRates, VehicleRatesValidate } from '@autos/entities/vehicle';
import { isEmpty } from 'lodash-es';

import { isNullOrUndefined } from '@app/util/util';
import { AutoCategorySlide } from '@autos/auto-results/entities/auto-category-slide';
import { ProductProperties } from '@avo/interface/trigger-props';
import { AutoSearchResultsData } from '@autos/auto-results/entities/auto-search-results-data';
import {
  AutoPlaceParams, AutoURLParams
} from '@autos/auto-results/entities/autoURLParams';
import { formatDateTime } from '@autos/auto-results/helpers/auto.helpers';
import {
  AutoFiltersPayloadState
} from '@autos/auto-results/store/reducers/auto-filters-payload.reducer';
import { AutoResultsState } from '@autos/auto-results/store/reducers/auto-results.reducer';
import { VehicleOption, VehicleOptionDetailValidate } from '@autos/entities/vehicle';
import { VehicleMarker } from '@autos/entities/vehicle-marker';
import { VehicleResultsPayload } from '@autos/entities/vehicle-results.payload';
import { DropOffSearch, PickUpSearch } from '@autos/entities/vehicle-search.payload';
import { environment } from '@environment';
import { currencyFormat } from '@hotels/hotel-results/helpers/hotels.helpers';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { SearchData } from '@shared/base-search-bar/entities/search-data';

export function mapFiltersCategoriesResultsPayload(state: AutoFiltersPayloadState): VehicleResultsPayload {
  return {
    ResultCountLowerBound: 0,
    ResultCountUpperBound: state.ResultsCount,
    OrderCriteria: state.OrderCriteria,
    OrderDesc: state.OrderDesc,
    SearchId: state.SearchId,
    SessionToken: state.SessionToken
  };
}

export function mapAutoSearchData(state: AutoResultsState): AutoSearchResultsData {
  const { ResultsAvailableUntil, SearchId, ResultsCount, session } = state;
  return { ResultsAvailableUntil, SearchId, ResultsCount, session };
}

export function mapSearchAutoResultsPaginate
(state: AutoResultsState, autos: VehicleOption[]): AutoResultsState {
  return {
    ...state,
    Results: state.Results.concat(autos),
    ResultsCount: autos.length
  };
}

export function mapSearchAutoCategoriesResults
(state: AutoResultsState, categories: AutoCategorySlide[]): AutoResultsState {
  return {
    ...state,
    Categories: categories
  };
}

export function mapSearchAutoMarkers
(state: AutoResultsState, autos: VehicleOption[]): AutoResultsState {
  return {
    ...state,
    ResultsMarkers: autos
  };
}

export function mapAutoMarkers(autos: VehicleOption[]): VehicleMarker[] {
  return !isNullOrUndefined(autos) ? autos.map((auto: VehicleOption) => {
    return {
      auto,
      position: { lat: auto.PickUp.Latitude || 0, lng: auto.PickUp.Longitude || 0 },
      label: currencyFormat(auto.Rates[0].PricePayNow.Total)
    };
  }) : autos as VehicleMarker[];
}

export function mapSearchAutoResults(payload: AutoFiltersPayloadState): VehicleResultsPayload {
  return {
    ...payload,
    ResultCountLowerBound: null,
    ResultCountUpperBound: null,
  };
}

export function mapSearchURLParams(searchData: SearchData, category?: string): AutoURLParams {
  const mapped: AutoURLParams = {
    departureDate: formatDateTime(searchData.departureDate, searchData.pickUpTime),
    departurePlaceCode: searchData.pickUpPlace.code,
    departureIsAirport: searchData.pickUpPlace.is_airport,
    arrivalDate: formatDateTime(searchData.arrivalDate, searchData.dropOffTime),
    arrivalPlaceCode: (searchData.dropOffPlace?.code) ? searchData.dropOffPlace.code : searchData.pickUpPlace.code,
    arrivalIsAirport: searchData.dropOffPlace.is_airport,
    currencyCode: `${environment.countryInfo.currency_code}`
  };
  if (searchData.pickUpPlace) {
    mapped.pickUp = mapAutoPlace(searchData.pickUpPlace);
  }
  if (searchData.dropOffPlace) {
    mapped.dropOff = mapAutoPlace(searchData.dropOffPlace);
  }
  if (category && category !== '') {
    mapped.category = category;
  }
  return mapped;
}

export function mapURLParams(pickUp: PickUpSearch, dropOff: DropOffSearch): AutoURLParams {
  const mapped: AutoURLParams = {
    departureDate: pickUp.PickUpDateTime,
    departurePlaceCode: pickUp.DestinationCode,
    departureIsAirport: pickUp.PickUpType === 'Airport',
    arrivalDate: dropOff.DropOffDateTime,
    arrivalPlaceCode: dropOff.DestinationCode,
    arrivalIsAirport: dropOff.DropOffType === 'Airport',
    currencyCode: `${environment.countryInfo.currency_code}`
  };
  return mapped;
}

function mapAutoPlace(place: SelectAutocompletePrediction): AutoPlaceParams {
  return {
    placeId: place.placeId,
    lat: place.lat,
    lng: place.lng
  };
}

export function mapStringAutoPlace(place: string): AutoPlaceParams {
  if (place && place.indexOf(',') > -1) {
    const aux = place.split(',');
    return {
      placeId: aux[0],
      lat: Number(aux[1]),
      lng: Number(aux[2])
    };
  }
  return null;
}

export function autoAmplitudeProductPropertiesMapper(data: VehicleOption | VehicleOptionDetailValidate): ProductProperties {
  if (!data) return {};
  const rates = data.Rates[0];
  const price = ('PricePayNow' in rates) ? (rates as VehicleRates).PricePayNow.Total :
    (rates as VehicleRatesValidate).TotalPricePayNow;
  return {
    TRAVEL_PRODUCT_CODE: data.Company.Code,
    TRAVEL_PRODUCT_NAME: data.Company.Name,
    TRAVEL_SUB_PRODUCT: data.Vehicle.Model,
    TRAVEL_PRODUCT_CATEGORY: data.Vehicle.VehicleCategory,
    TRAVEL_PRODUCT_PRICE: price,
    TRAVEL_PRODUCT_PROVIDER: data.Provider,
    TRAVEL_TOTAL_PRICE: price,
  };
}

export function mapAutoResultsSummary(autoResults: VehicleOption[]): VehicleOption[] {
  if (isEmpty(autoResults) || autoResults.length === 0) return [];
  const tempResults: { vehicle: VehicleOption, order: number }[] = [];
  const aux = {};
  const origin = [...autoResults];
  const copy = [...autoResults];
  copy.sort((a, b) => a.Rates[0].PricePayNow.Total - b.Rates[0].PricePayNow.Total);
  copy.forEach(option => {
    const companyKey = option.Company.Code;
    aux[companyKey] = aux[companyKey] || [];
    if (!aux[companyKey].includes(option.Vehicle.Model)) {
      aux[companyKey].push(option.Vehicle.Model);
      const preferentialOption = copy.find(o =>
        o.Company.Code === option.Company.Code &&
        o.Vehicle.Model === option.Vehicle.Model &&
        o.Rates.some(rate => rate.PreferentialRate)
      );
      const selectedOption = preferentialOption || option;
      tempResults.push({
        vehicle: selectedOption,
        order: origin.findIndex(o => o.OptionId === selectedOption.OptionId)
      });
    }
  });
  tempResults.sort((a, b) => a.order - b.order);
  return tempResults.map(o => o.vehicle);
}


